import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

export interface FilterState {
  genre: string;
  region: string;
  year: string;
}

export const defaultFilters: FilterState = { genre: "全部", region: "全部", year: "全部" };

interface FilterPanelProps {
  genreOptions: string[];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  mobile?: boolean;
  onClose?: () => void;
}

const rows = [
  { key: "genre" as const, label: "类型" },
  { key: "region" as const, label: "地区" },
  { key: "year" as const, label: "年份" },
];

export default function FilterPanel({
  genreOptions,
  filters,
  onChange,
  onReset,
  mobile = false,
  onClose,
}: FilterPanelProps) {
  const optionsOf = (key: keyof FilterState) => {
    if (key === "genre") return ["全部", ...genreOptions];
    if (key === "region") return ["全部", "国产", "欧美", "日韩"];
    return ["全部", "2026", "2025", "2024"];
  };

  const body = (
    <div className={`space-y-4 ${mobile ? "" : "p-4 rounded-lg border border-background-200 bg-background-50"}`}>
      {rows.map((row) => (
        <div key={row.key} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
          <span className="text-xs text-foreground-500 sm:w-10 shrink-0 sm:pt-1.5">{row.label}</span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {optionsOf(row.key).map((option) => {
              const active = filters[row.key] === option;
              return (
                <button
                  key={`${row.key}-${option}`}
                  type="button"
                  onClick={() => onChange({ ...filters, [row.key]: option })}
                  className={`px-3 h-8 rounded-md text-[13px] whitespace-nowrap transition-colors ${
                    active
                      ? "bg-secondary-500 text-foreground-950 font-medium"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2 border-t border-background-200">
        <p className="text-xs text-foreground-500">
          已选：{filters.genre} / {filters.region} / {filters.year}
        </p>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );

  if (!mobile) return body;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background-50 md:hidden">
      <div className="flex items-center justify-between px-4 h-14 border-b border-background-200">
        <h2 className="font-heading text-base font-semibold">筛选</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭筛选"
          className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">{body}</div>
      <div className="p-4 border-t border-background-200">
        <button
          type="button"
          onClick={onClose}
          className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap"
        >
          查看结果
        </button>
      </div>
    </div>
  );
}